import { useEffect } from 'react'
import { MapContainer as LeafletMapContainer, TileLayer, ZoomControl } from 'react-leaflet'
import { useLayerStore } from '@/stores/layerStore'
import { LAYER_CONFIGS } from '@/data/layerConfig'
import { useGeoJSON, prefetchGeoJSON } from '@/hooks/useGeoJSON'
import { GeoJSONLayer } from './GeoJSONLayer'
import { UpzoneScenarioLayer } from '@/components/layers/UpzoneScenarioLayer'
import { UnderUtilizationLayer } from '@/components/layers/UnderUtilizationLayer'
import { FireRiskLayer } from '@/components/layers/FireRiskLayer'
import { CommercialViabilityLayer } from '@/components/layers/CommercialViabilityLayer'
import { RetrofitLayer } from '@/components/layers/RetrofitLayer'
import type {
  UpzoneCollection,
  UnderUtilizationCollection,
  FireRiskCollection,
  CommercialViabilitySiteCollection,
  RetrofitLayerId,
  ParkingRetrofitCollection,
  VacantInfillCollection,
  CommercialRetrofitCollection,
} from '@/types'

// Chico, CA coordinates
const CHICO_CENTER: [number, number] = [39.7285, -121.8375]
const DEFAULT_ZOOM = 12

/**
 * Renders a layer if it's visible and data is available
 */
function LayerRenderer({ layerId }: { layerId: string }) {
  const config = LAYER_CONFIGS.find(c => c.id === layerId)
  const visibleLayers = useLayerStore(state => state.visibleLayers)
  const layerOpacity = useLayerStore(state => state.layerOpacity)

  const isVisible = visibleLayers.has(layerId)
  const opacity = layerOpacity[layerId] ?? 1

  const { data, loading, error } = useGeoJSON(
    config?.dataUrl ?? '',
    isVisible && !!config
  )

  if (!config || !isVisible || loading || error || !data) {
    return null
  }

  // Use specialized layer component for upzone scenario
  if (layerId === 'upzone-scenario') {
    return <UpzoneScenarioLayer data={data as UpzoneCollection} opacity={opacity} />
  }

  // Use specialized layer component for under-utilization
  if (layerId === 'under-utilization') {
    return <UnderUtilizationLayer data={data as UnderUtilizationCollection} opacity={opacity} />
  }

  // Use specialized layer component for fire risk
  if (layerId === 'fire-risk-index') {
    return <FireRiskLayer data={data as FireRiskCollection} opacity={opacity} />
  }

  // Use specialized layer component for commercial viability
  if (layerId === 'commercial-viability') {
    return (
      <CommercialViabilityLayer
        data={data as CommercialViabilitySiteCollection}
        opacity={opacity}
      />
    )
  }

  // Use specialized layer component for retrofit scenarios
  const RETROFIT_LAYERS: RetrofitLayerId[] = [
    'parking-retrofit',
    'vacant-infill',
    'commercial-retrofit',
  ]
  if (RETROFIT_LAYERS.includes(layerId as RetrofitLayerId)) {
    return (
      <RetrofitLayer
        layerId={layerId as RetrofitLayerId}
        data={data as ParkingRetrofitCollection | VacantInfillCollection | CommercialRetrofitCollection}
        opacity={opacity}
      />
    )
  }

  return <GeoJSONLayer config={config} data={data} opacity={opacity} />
}

export function MapContainer() {
  // Prefetch all layer data on mount for snappier layer toggling
  useEffect(() => {
    LAYER_CONFIGS.forEach(config => {
      prefetchGeoJSON(config.dataUrl).catch(() => {
        // Silently ignore prefetch errors - data will be fetched when layer is enabled
      })
    })
  }, [])

  return (
    <LeafletMapContainer
      center={CHICO_CENTER}
      zoom={DEFAULT_ZOOM}
      zoomControl={false}
      className="h-full w-full"
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        subdomains="abcd"
        maxZoom={20}
      />

      {/* Render all configured layers */}
      {LAYER_CONFIGS.map(config => (
        <LayerRenderer key={config.id} layerId={config.id} />
      ))}

      <ZoomControl position="bottomright" />
    </LeafletMapContainer>
  )
}
